import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_ALL_USERS } from '../graphql/getAllUsers';
import { RESET_FINGERPRINT } from '../graphql/resetFingerprint';
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Google as GoogleIcon,
  Fingerprint as FingerprintIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { AdminStyles } from '../styles/AdminStyles';
import { CommonStyles } from '../styles/CommonStyles';
import { Role } from '../__generated__/graphql';

// Helper function to safely format dates
const formatPlanExpiryDate = (dateString?: string | null): string => {
  if (!dateString) return 'No Plan';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    return date.toLocaleDateString();
  } catch (error) {
    return 'Invalid Date';
  }
};

interface User {
  id: string;
  name: string;
  email: string;
  role: Role;
  isGoogleAuth: boolean;
  hasFingerprint: boolean;
  planExpiresAt?: string;
  planName?: string;
}

const AdminUsers = () => {
  const { data, loading, error, refetch } = useQuery(GET_ALL_USERS);
  const [resetFingerprint] = useMutation(RESET_FINGERPRINT);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    userId: string;
    userName: string;
  }>({ open: false, userId: '', userName: '' });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const handleResetFingerprint = async (userId: string) => {
    try {
      await resetFingerprint({
        variables: { userId },
      });
      refetch(); // Refresh the data after reset
      setSnackbar({
        open: true,
        message: 'Fingerprint reset successfully',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error resetting fingerprint:', err);
      setSnackbar({
        open: true,
        message: 'Failed to reset fingerprint',
        severity: 'error',
      });
    }
  };

  const filteredUsers =
    data?.getAllUsers?.filter(
      (user: User) =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  if (loading) {
    return (
      <Box sx={CommonStyles.loadingContainer}>
        <CircularProgress size={70} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={CommonStyles.loadingContainer}>
        <Typography color="error">
          Error loading users: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...AdminStyles.container, flexDirection: 'column' }}>
      {/* Header Section */}
      <Box sx={AdminStyles.welcomeSection}>
        <Typography variant="h4" sx={AdminStyles.title}>
          User Management
        </Typography>
        <Typography sx={AdminStyles.subtitle}>
          Manage user accounts, monitor activity, and reset browser fingerprints
        </Typography>
      </Box>

      {/* Search and Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          maxWidth: '1200px',
          marginBottom: '24px',
          gap: '16px',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            minWidth: '400px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <Button
            variant={viewMode === 'table' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('table')}
            startIcon={<PersonIcon />}
            sx={{ borderRadius: '8px' }}
          >
            Table View
          </Button>
          <Button
            variant={viewMode === 'cards' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('cards')}
            startIcon={<PersonIcon />}
            sx={{ borderRadius: '8px' }}
          >
            Card View
          </Button>
        </Box>
      </Box>

      {/* User Count */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '1200px',
          marginBottom: '16px',
          textAlign: 'left',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {filteredUsers.length} of {data?.getAllUsers?.length || 0}{' '}
          users
        </Typography>
      </Box>

      {/* Table View */}
      {viewMode === 'table' && (
        <TableContainer
          component={Paper}
          sx={{
            ...AdminStyles.tableContainer,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Table stickyHeader aria-label="users table">
            <TableHead sx={AdminStyles.tableHeader}>
              <TableRow>
                <TableCell sx={AdminStyles.tableHeaderCell}>User</TableCell>
                <TableCell sx={AdminStyles.tableHeaderCell}>Email</TableCell>
                <TableCell sx={AdminStyles.tableHeaderCell}>Role</TableCell>
                <TableCell sx={AdminStyles.tableHeaderCell}>
                  Auth Type
                </TableCell>
                <TableCell sx={AdminStyles.tableHeaderCell}>
                  Plan Expiry
                </TableCell>
                <TableCell sx={AdminStyles.tableHeaderCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user: User) => (
                <TableRow
                  key={user.id}
                  hover
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <TableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor:
                            user.role === Role.Admin
                              ? 'primary.main'
                              : '#1e3a8a',
                          width: 40,
                          height: 40,
                        }}
                      >
                        {user.role === Role.Admin ? (
                          <AdminIcon />
                        ) : (
                          <PersonIcon />
                        )}
                      </Avatar>
                      <Typography variant="body1" fontWeight="500">
                        {user.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2">{user.email}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.role}
                      color={user.role === Role.Admin ? 'primary' : 'default'}
                      size="small"
                      icon={
                        user.role === Role.Admin ? (
                          <AdminIcon />
                        ) : (
                          <PersonIcon />
                        )
                      }
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      {user.isGoogleAuth ? (
                        <>
                          <GoogleIcon fontSize="small" color="primary" />
                          <Typography variant="body2">Google</Typography>
                        </>
                      ) : (
                        <>
                          <SecurityIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            Email/Password
                          </Typography>
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      {user.planExpiresAt ? (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            {user.planName || 'Unknown Plan'}
                          </Typography>
                          <Typography variant="body2" fontWeight="500">
                            {formatPlanExpiryDate(user.planExpiresAt)}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No Plan
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user.hasFingerprint && (
                      <Tooltip title="Reset browser fingerprint">
                        <Button
                          variant="outlined"
                          color="warning"
                          size="small"
                          startIcon={<FingerprintIcon />}
                          onClick={() =>
                            setConfirmDialog({
                              open: true,
                              userId: user.id,
                              userName: user.name,
                            })
                          }
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: '500',
                          }}
                        >
                          Reset Browser
                        </Button>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Card View */}
      {viewMode === 'cards' && (
        <Grid container spacing={3} sx={{ width: '100%', maxWidth: '1200px' }}>
          {filteredUsers.map((user: User) => (
            <Grid item xs={12} sm={6} md={4} key={user.id}>
              <Card
                sx={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      mb: 2,
                    }}
                  >
                    <Avatar
                      sx={{
                        backgroundColor:
                          user.role === Role.Admin ? 'primary.main' : '#1e3a8a',
                        width: 56,
                        height: 56,
                      }}
                    >
                      {user.role === Role.Admin ? (
                        <AdminIcon />
                      ) : (
                        <PersonIcon />
                      )}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {user.name}
                      </Typography>
                      <Chip
                        label={user.role}
                        color={user.role === Role.Admin ? 'primary' : 'default'}
                        size="small"
                        icon={
                          user.role === Role.Admin ? (
                            <AdminIcon />
                          ) : (
                            <PersonIcon />
                          )
                        }
                      />
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        mb: 1,
                      }}
                    >
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      {user.isGoogleAuth ? (
                        <>
                          <GoogleIcon fontSize="small" color="primary" />
                          <Typography variant="body2" color="text.secondary">
                            Google Authentication
                          </Typography>
                        </>
                      ) : (
                        <>
                          <SecurityIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            Email/Password Authentication
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      {user.planExpiresAt ? (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            Plan: {user.planName || 'Unknown Plan'}
                          </Typography>
                          <Typography variant="body2" fontWeight="500">
                            Expires: {formatPlanExpiryDate(user.planExpiresAt)}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No Plan
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {user.hasFingerprint && (
                    <Button
                      variant="outlined"
                      color="warning"
                      fullWidth
                      startIcon={<FingerprintIcon />}
                      onClick={() =>
                        setConfirmDialog({
                          open: true,
                          userId: user.id,
                          userName: user.name,
                        })
                      }
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: '500',
                      }}
                    >
                      Reset Browser Fingerprint
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({ open: false, userId: '', userName: '' })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}
        >
          <WarningIcon color="warning" />
          Confirm Fingerprint Reset
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reset the browser fingerprint for{' '}
            <strong>{confirmDialog.userName}</strong>? This will require them to
            re-authenticate their browser.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({ open: false, userId: '', userName: '' })
            }
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleResetFingerprint(confirmDialog.userId);
              setConfirmDialog({ open: false, userId: '', userName: '' });
            }}
            color="warning"
            variant="contained"
            startIcon={<RefreshIcon />}
          >
            Reset Fingerprint
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsers;
